// src/components/RightSideButtons/RightSideButtons.tsx
import React, {
  FC, ReactNode, useEffect, useState,
} from 'react';
import { EllipsisOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import './RightSideButtons.less';

interface RightSideButtonsProps {
  children: ReactNode;
  className?: string;
}

// Opcje gry schowane pod przyciskiem „⋯” w prawym górnym rogu.
// Przy zamkniętym menu przyciski zostają zamontowane, tylko niewidoczne: reguły i edycja
// zwrotu otwierają okna przez portal, które zniknęłyby razem z przyciskiem.
export const RightSideButtons: FC<RightSideButtonsProps> = ({
  children,
  className = '',
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Wybór opcji zamyka menu, chyba że przycisk ma `data-keep-menu-open` (przełącznik lektora —
  // nowy stan ma być od razu widoczny). Kliknięcia w oknach otwartych przez portal też tu
  // docierają, bo React przenosi zdarzenia przez portal — te pomijamy.
  const handleMenuClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const option = (event.target as Element).closest('button');

    if (!option || !event.currentTarget.contains(option)) return;
    if (option.hasAttribute('data-keep-menu-open')) return;
    setIsOpen(false);
  };

  return (
    <div className={`right-side-buttons ${isOpen ? 'open' : ''} ${className}`}>
      {/* Kliknięcie obok otwartego menu tylko je zamyka — nie trafia w grę pod spodem */}
      {isOpen && (
        <div className="options-backdrop" role="presentation" onClick={() => setIsOpen(false)} />
      )}

      <button
        type="button"
        className="options-toggle"
        onClick={() => setIsOpen((current) => !current)}
        aria-label={t('games:labels.moreOptions')}
        aria-expanded={isOpen}
      >
        <EllipsisOutlined />
      </button>

      <div className="options-menu" role="presentation" onClick={handleMenuClick}>
        {children}
      </div>
    </div>
  );
};
